import React, { useState, useEffect } from 'react';
import 'react-datepicker/dist/react-datepicker.css';
import './MessageLogs.css';
import axios from 'axios';
import { useMsal } from '@azure/msal-react';
import FilterBar from '../FilterComponents/FilterBar';
import DialogOkCancelFormComponent from '../Modal/DialogOkCancelFormComponent';
import { resendFile } from '../../helpers/fileHelper';
import { getApiKey } from '../../helpers/stringHelpers';
import { getYesterday, getTomorrow } from '../../helpers/dateTimeHelper';
import { useDropdownFilters } from '../../hooks/useDropdownFilters';
import LogTable from '../LogTableComponents/LogTable';

function UniqErrorMsg({ config }) {
    const inital_maxRows = 100;

    const { accounts, instance } = useMsal();

    //Init states
    const [logMessageTable, setLogMessageTable] = useState([]);

    const [inputValue, setInputValue] = useState('');
    const [filterWords, setFilterWords] = useState('');
    const [dateFrom, setDateFrom] = useState(getYesterday());
    const [dateTo, setDateTo] = useState(getTomorrow());
    const [maxRows, setMaxRows] = useState(inital_maxRows);
    const [maxRowsInputValue, setMaxRowsInputValue] = useState(inital_maxRows);
    const [latestUpdate, setLatestUpdate] = useState(null);
    const [transactionTagFilter, setTransactionTagFilter] = useState(null);

    const [dropdownTable, setDropdownTable] = useState([]);

    const [loading, setLoading] = useState(false); // Loading log message table
    const [loadingDropdownOptions, setLoadingDropdownOptions] = useState(false); // Loading dropdown options

    const [inputEnterKey, setInputEnterKey] = useState(0); // Unique key for each enter input event
    const [maxRowsInputEnterKey, setMaxRowsInputEnterKey] = useState(0); // Unique key for each enter input event
    const [totalRows, setTotalRows] = useState(null);

    const [modalOpen, setModalOpen] = useState(false);
    const [modalData, setModalData] = useState("");

    // Confirmation Modal
    const openModal = () => {
        setModalOpen(true);
    };
    const closeModal = () => {
        setModalOpen(false);
    };
    const handleModalClose = () => {
        closeModal();
        setModalData("");
    };
    const handleModalOK = () => {
        resendFile(modalData, config, accounts, instance);
        closeModal();
    };
    // Resend file callback
    const handleResendFile = (modalData) => {
        setModalData(modalData);
        openModal();
    };
    // Handle filter changes
    const handleInputChange = (e) => {
        // Update the inputValue state when the input value changes
        setInputValue(e.target.value);
        setMaxRows(maxRowsInputValue);
    };
    const handleInputKeyPress = (e) => {
        if (e.key === 'Enter') {
            setFilterWords(e.target.value);
            setInputEnterKey(prevKey => prevKey + 1); // Update key to trigger useEffect
        }
    };
    const handleSearchClick = () => {
        setFilterWords(inputValue);
        setMaxRows(maxRowsInputValue);
    };
    // Date Picker changes
    const handleDateFromChange = (selectedDate) => {
        setDateFrom(selectedDate);

        // If the new dateFrom is >= dateTo, set dateTo to the next day
        if (!dateTo || selectedDate >= dateTo) {
            const nextDay = new Date(selectedDate);
            nextDay.setDate(nextDay.getDate() + 1);
            setDateTo(nextDay);
        }
    };
    const handleDateToChange = (selectedDate) => {
        setDateTo(selectedDate);

        // If the new dateTo is <= dateFrom, set dateFrom to the previous day
        if (!dateFrom || selectedDate <= dateFrom) {
            const prevDay = new Date(selectedDate);
            prevDay.setDate(prevDay.getDate() - 1);
            setDateFrom(prevDay);
        }
    };
    const toggleDatePicker = (id) => {
        const datePicker = document.getElementById(id);
        if (datePicker) {
            datePicker.focus();
        }
    };
    // Handle max rows input changes
    const handleMaxRowsChange = (e) => {
        // Update the inputValue state when the input value changes
        setMaxRowsInputValue(e.target.value);
    };
    const handleMaxRowsKeyPress = (e) => {
        if (e.key === 'Enter') {
            setMaxRows(e.target.value);
            setMaxRowsInputEnterKey(prevKey => prevKey + 1); // Update key to trigger useEffect
        }
    };

    // Handler for clicking a transactionTag
    const handleTransactionTagClick = (tag) => {
        setTransactionTagFilter(tag);
    };

    // Optionally, add a button to clear the filter
    const handleClearTransactionTagFilter = () => {
        setTransactionTagFilter(null);
    };


    // Handle dropdown filter changes
    const dropdownDaysAgo = 30;
    const dropdownFields = ["sender", "receiver", "transactionType"];

    const {
        dropdownOptions,
        selectedValues,
        handleDropdownChange
    } = useDropdownFilters(dropdownFields, dropdownTable);

    const tableHeaders = [
        { title: "#", isDropdown: false, maxWidth: "25px" },
        { title: "Processed", isDropdown: false, maxWidth: "67px" },
        { title: "Sender", isDropdown: true, field: "sender", maxWidth: "80px" },
        { title: "Receiver", isDropdown: true, field: "receiver", maxWidth: "80px" },
        { title: "Transaction Type", isDropdown: true, field: "transactionType", maxWidth: "80px" },
        { title: "Message Type", isDropdown: false, field: "messageType", maxWidth: "70px" },
        { title: "Transaction Tag", isDropdown: false, field:"transactionTag", maxWidth: "70px" },
        { title: "Description", isDropdown: false, maxWidth: "400px" },
        { title: "Status", isDropdown: false, maxWidth: "50px" },
        { title: "FileName", isDropdown: false, maxWidth: "70px" },
        { title: "Scope", isDropdown: false, maxWidth: "40px" },
        { title: "System", isDropdown: false, maxWidth: "40px" },
        { title: "Snapshot", isDropdown: false, maxWidth: "10px" }
    ];

    // Fetch dropdown data on component mount
    useEffect(() => {
        setLoadingDropdownOptions(true);
        const applicationId = config.clientId;

        const accessTokenRequest = {
            scopes: ["api://" + applicationId + "/User.Read"],
            account: accounts[0],
        };

        instance
            .acquireTokenSilent(accessTokenRequest)
            .then((accessTokenResponse) => {
                const accessToken = accessTokenResponse.accessToken;
                const logmsgApiUrl = config.logMsgApiUrl;
                const apiKey = getApiKey(config.enviromentScope);

                const getRequest = logmsgApiUrl + '/LogsGetGroup?days=' + dropdownDaysAgo + '&status=error&type=6';
                //const getRequest = `${logmsgApiUrl}/LogsGetGroup?days=${dropdownDaysAgo}&status=error&type=7`; //Type 7: Include messageType in response

                const headers = {
                    Authorization: `Bearer ${accessToken}`,
                    Accept: 'application/json',
                    'Content-Type': 'application/json',
                    APIKEY: apiKey
                };

                axios.get(getRequest, { headers })
                    .then((response) => {
                        setDropdownTable(response.data);
                        console.log(response.data);
                    })
                    .catch((error) => console.error(error))
                    .finally(() => setLoadingDropdownOptions(false));
            })
            .catch((error) => console.error(error));
    }, []);

    // Find all log messages from API, based on filters: word, dates and dropdown.
    // If any of these filter changes this useEffect should run
    useEffect(() => {
        setLoading(true); // Set loading to true when starting to fetch data

        const applicationId = config.clientId;

        const accessTokenRequest = {
            scopes: ["api://" + applicationId + "/User.Read"],
            account: accounts[0],
        };
        const fetchLogMsgTableData = () => {
            instance
                .acquireTokenSilent(accessTokenRequest)
                .then((accessTokenResponse) => {
                    // Acquire token silent success
                    let accessToken = accessTokenResponse.accessToken;
                    // Call your API with token
                    // Get config values

                    const logmsgApiUrl = config.logMsgApiUrl;
                    const apiKey = getApiKey(config.enviromentScope);

                    // Setup GET query
                    let getRequest = logmsgApiUrl + '/LogsGetAllNoBodyLastIdError?';

                    // Setup Header
                    const headers = {
                        'Authorization': 'Bearer ' + accessToken,
                        'Accept': 'application/json',
                        'Content-Type': 'application/json',
                        'APIKEY': apiKey
                    };

                    // Update Params based on state of the filters
                    const updateQueryParams = () => {
                        const APIKEY = apiKey;
                        const dtFrom = dateFrom.toISOString().split('T')[0];
                        const dtTo = dateTo.toISOString().split('T')[0];
                        const filter = filterWords;

                        // Dynamically add dropdown fields to query parameters
                        const dropdownParams = dropdownFields.reduce((params, field) => {
                            params[field] = selectedValues[field] !== "All" ? selectedValues[field] : null;
                            return params;
                        }, {});

                        // Add transactionTag filter if set
                        if (transactionTagFilter) {
                            dropdownParams.transactionTag = transactionTagFilter;
                        }

                        return { APIKEY, dtFrom, dtTo, maxRows: maxRows, filter, ...dropdownParams };
                    };
                    const queryParams = updateQueryParams();

                    // Make the API request
                    axios.get(getRequest, {
                        params: queryParams,
                        headers: headers
                    })
                        .then((response) => {
                            // Set the fetched data to the logMessageTable variable
                            if (response.data === "" || response.data === null) {
                                setLogMessageTable([]);
                                setTotalRows(0);
                            } else {
                                setLogMessageTable(response.data);
                                setTotalRows(response.data.length);
                                setLatestUpdate(new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: false }));
                            }
                        })
                        .catch((error) => {
                            // Handle errors
                            console.log(error);
                        })
                        .finally(() => {
                            setLoading(false); // Set loading to false when data fetching is completed (success or failure)
                        });
                })
                .catch((error) => {
                    console.log(error);
                });
        };
        // Fetch immediately on mount
        fetchLogMsgTableData();

        // Set interval to refresh every 5 minutes (300000 ms)
        const interval = setInterval(fetchLogMsgTableData, 300000);

        // Cleanup interval on component unmount
        return () => clearInterval(interval);
    }, [dateFrom, dateTo, filterWords, selectedValues, inputEnterKey, maxRows, maxRowsInputEnterKey, transactionTagFilter]); // Use dependency array to run the effect only when at least one of these values are changed

    return (
        <div className="message-logs-container">
            <h2 className="message-logs-title">Uniq Error Logs</h2>

            {/* Filter inputs in a horizontal line */}
            <FilterBar
                config={config}
                inputValue={inputValue}
                handleInputChange={handleInputChange}
                handleInputKeyPress={handleInputKeyPress}
                handleSearchClick={handleSearchClick}
                dateFrom={dateFrom}
                handleDateFromChange={handleDateFromChange}
                toggleDatePicker={toggleDatePicker}
                dateTo={dateTo}
                handleDateToChange={handleDateToChange}
                maxRowsInputValue={maxRowsInputValue}
                handleMaxRowsChange={handleMaxRowsChange}
                handleMaxRowsKeyPress={handleMaxRowsKeyPress}
                totalRows={totalRows}
                latestUpdate={latestUpdate}
            />

            <LogTable
                loading={loading}
                userconfig={"_UniqErrorMsg_" + (accounts[0]?.username || 'Unknown User')}
                tableHeaders={tableHeaders}
                selectedValues={selectedValues}
                dropdownOptions={dropdownOptions}
                handleDropdownChange={handleDropdownChange}
                loadingDropdownOptions={loadingDropdownOptions}
                logMessageTable={logMessageTable}
                config={config}
                modalOpen={modalOpen}
                openModal={openModal}
                closeModal={closeModal}
                handleResendFile={handleResendFile}
                accounts={accounts}
                instance={instance}
                onTransactionTagClick={handleTransactionTagClick}
                transactionTagFilter={transactionTagFilter}
                handleClearTransactionTagFilter={handleClearTransactionTagFilter}
            />

            <DialogOkCancelFormComponent
                isOpen={modalOpen}
                onRequestClose={handleModalClose}
                TextTitle={modalData.TextTitle}
                TextBody1={modalData.TextBody1}
                TextBody2={modalData.TextBody2}
                TextBody3={modalData.TextBody3}
                TextBody4={modalData.TextBody4}
                TextBody5={modalData.TextBody5}
                onResult={handleModalOK}
                onClose={handleModalClose}
            />
        </div>
    );
}

export default UniqErrorMsg;
